import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { convertCurrency } from "../helper/CurrencyConverter";
import HeaderOne from "../components/HeaderOne";
import FooterOne from "../components/FooterOne";

const MAX_SWATCHES = 5;

/* ─── Skeleton Card (matches pl-card shape) ────────────────── */
const SkeletonCard = () => (
  <div className="pl-card vp-skeleton-card">
    <div className="pl-card__thumb vp-skel" />
    <div className="pl-card__content">
      <div className="vp-skel vp-skel--title" />
      <div className="vp-skel vp-skel--sub" />
      <div className="vp-skel vp-skel--price" />
    </div>
  </div>
);

/* ─── Single Product Card (identical to ShopSectionOne) ────── */
const ProductCard = ({ item, selectedCurrency }) => {
  const variants = item.variants || [];

  const colorVariants = variants.filter(
    (v) => v.color || v.colorName || v.colorCode || v.colorHex,
  );

  const hasColors = colorVariants.length > 0;
  const [activeIdx, setActiveIdx] = useState(-1);

  const activeVariant = activeIdx >= 0 ? colorVariants[activeIdx] : null;

  const displayImage =
    activeVariant?.images?.[0] ||
    activeVariant?.image ||
    item.images?.[0] ||
    "assets/images/thumbs/product-img1.png";

  const variantPrice =
    activeVariant?.price || item?.variants?.[0]?.price || item?.price || {};

  const displayPrice =
    typeof variantPrice === "object"
      ? variantPrice?.[selectedCurrency] || variantPrice?.INR
      : variantPrice || 0;

  const variantOldPrice =
    activeVariant?.oldPrice ||
    item?.variants?.[0]?.oldPrice ||
    item?.oldPrice ||
    null;

  const displayOldPrice =
    typeof variantOldPrice === "object"
      ? variantOldPrice?.[selectedCurrency] || variantOldPrice?.INR
      : variantOldPrice;

  const reviews = item.reviews || [];
  const total = reviews.length;
  const avg =
    total > 0
      ? (reviews.reduce((s, r) => s + (r.ratings || 0), 0) / total).toFixed(1)
      : "0.0";

  const swatchColor = (v) =>
    v.colorHex || v.colorCode || v.color || v.colorName || "#ccc";

  const visibleSwatches = colorVariants.slice(0, MAX_SWATCHES);
  const extraCount = colorVariants.length - MAX_SWATCHES;

  return (
    <div className="pl-card">
      {/* Image */}
      <Link to={`/product-details/${item._id}`} className="pl-card__thumb">
        <img src={displayImage} alt={item.name} className="pl-card__img" />
      </Link>

      {/* Colour swatches */}
      {hasColors && (
        <div className="pl-swatches">
          {visibleSwatches.map((v, i) => (
            <button
              key={i}
              className={`pl-swatch ${activeIdx === i ? "pl-swatch--active" : ""}`}
              style={{ background: swatchColor(v) }}
              title={v.colorName || v.color || `Color ${i + 1}`}
              onClick={() => setActiveIdx(activeIdx === i ? -1 : i)}
              aria-label={`Select color ${v.colorName || i + 1}`}
            />
          ))}
          {extraCount > 0 && (
            <Link
              to={`/product-details/${item._id}`}
              className="pl-swatch-more"
            >
              +{extraCount}
            </Link>
          )}
        </div>
      )}

      {/* Content */}
      <div className="pl-card__content">
        <h6 className="pl-card__name">
          <Link to={`/product-details/${item._id}`} title={item.name}>
            {item.name}
          </Link>
        </h6>

        <div className="pl-card__rating">
          <span className="pl-rating__avg">{avg}</span>
          <span className="pl-rating__star">
            <i className="ph-fill ph-star" />
          </span>
          <span className="pl-rating__count">({total})</span>
        </div>

        <div className="pl-card__price">
          {displayOldPrice && (
            <span className="pl-price--old">
              {selectedCurrency}{" "}
              {convertCurrency(displayOldPrice, selectedCurrency).toFixed(2)}
            </span>
          )}
          <span className="pl-price--now">
            {selectedCurrency} {displayPrice}
            <span className="pl-qty"> /Qty</span>
          </span>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────────── */
const VendorProducts = ({ selectedCurrency }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("default");

  useEffect(() => {
    if (!id) return;
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [prodRes, vendorRes] = await Promise.allSettled([
          axios.get(
            `${process.env.REACT_APP_API_URL}/seller/seller-products/vendor/${id}`,
          ),
          axios.get(`${process.env.REACT_APP_API_URL}/seller/${id}`),
        ]);
        if (prodRes.status === "fulfilled") {
          const data =
            prodRes.value.data.products || prodRes.value.data.data || [];
          setProducts(data.filter((p) => p.status === "Active"));
        }
        if (vendorRes.status === "fulfilled") {
          setVendor(vendorRes.value.data.seller);
        }
      } catch (err) {
        console.error("Error fetching vendor data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]);

  /* ── sort ── */
  const sorted = [...products].sort((a, b) => {
    if (sortBy === "price-asc") return a.price - b.price;
    if (sortBy === "price-desc") return b.price - a.price;
    if (sortBy === "rating") {
      const avg = (p) =>
        p.reviews?.length
          ? p.reviews.reduce((s, r) => s + (r.ratings || 0), 0) /
            p.reviews.length
          : 0;
      return avg(b) - avg(a);
    }
    return 0;
  });

  /* ── overall store rating ── */
  const allReviews = products.flatMap((p) => p.reviews || []);
  const storeRatingTotal = allReviews.length;
  const storeRatingAvg =
    storeRatingTotal > 0
      ? (
          allReviews.reduce((s, r) => s + (r.ratings || 0), 0) /
          storeRatingTotal
        ).toFixed(1)
      : null;

  return (
    <>
      <HeaderOne />

      <div className="vp-root">
        {/* ══════════════════════════════════════════════════
            SELLER BANNER
            ══════════════════════════════════════════════════ */}
        <div className="vp-banner">
          <div
            className="vp-banner__cover"
            style={
              vendor?.coverImage
                ? { backgroundImage: `url(${vendor.coverImage})` }
                : {}
            }
          >
            {!vendor?.coverImage && (
              <>
                <div className="vp-banner__blob vp-banner__blob--1" />
                <div className="vp-banner__blob vp-banner__blob--2" />
                <div className="vp-banner__blob vp-banner__blob--3" />
              </>
            )}
            <div className="vp-banner__scrim" />
          </div>

          <div className="vp-banner__body container container-lg">
            <div className="vp-banner__logo-wrap">
              {vendor?.logo || vendor?.avatar ? (
                <img
                  src={vendor.logo || vendor.avatar}
                  alt={vendor?.storeName || vendor?.name || "Seller"}
                  className="vp-banner__logo-img"
                />
              ) : (
                <div className="vp-banner__logo-placeholder">
                  <i className="ph-fill ph-storefront" />
                </div>
              )}
            </div>

            <div className="vp-banner__meta">
              <div className="vp-banner__name-row">
                <h1 className="vp-banner__store-name">
                  {vendor?.businessName || "Seller Store"}
                </h1>
                {vendor?.verified && (
                  <span className="vp-banner__verified" title="Verified Seller">
                    <i className="ph-fill ph-seal-check" /> Verified
                  </span>
                )}
              </div>

              <div className="vp-banner__stats">
                {storeRatingAvg && (
                  <span className="vp-banner__stat">
                    <i
                      className="ph-fill ph-star"
                      style={{ color: "#f0a500" }}
                    />
                    {storeRatingAvg} ({storeRatingTotal} reviews)
                  </span>
                )}
                <span className="vp-banner__stat">
                  <i className="ph-bold ph-package" />
                  {products.length} Products
                </span>
                {(vendor?.city || vendor?.country) && (
                  <span className="vp-banner__stat">
                    <i className="ph-bold ph-map-pin" />
                    {[vendor.city, vendor.country].filter(Boolean).join(", ")}
                  </span>
                )}
                {vendor?.createdAt && (
                  <span className="vp-banner__stat">
                    <i className="ph-bold ph-calendar" />
                    Since {new Date(vendor.createdAt).getFullYear()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabs bar ── */}
        <div className="vp-tabs-bar">
          <div className="container container-lg">
            <nav className="vp-tabs">
              <button className="vp-tab vp-tab--active">All Products</button>
            </nav>
          </div>
        </div>

        {/* ── Content (matches ShopSectionOne layout) ── */}
        <section className="shop py-80">
          <div className="container container-lg">
            {/* Toolbar */}
            <div className="vp-page-header">
              <p className="vp-page-sub">
                {loading
                  ? "Fetching products…"
                  : `${products.length} result${products.length !== 1 ? "s" : ""}`}
              </p>

              {!loading && products.length > 0 && (
                <div className="vp-toolbar">
                  <div className="vp-select-wrap">
                    <label className="vp-label">Sort by</label>
                    <select
                      className="vp-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="default">Featured</option>
                      <option value="price-asc">Price: Low to High</option>
                      <option value="price-desc">Price: High to Low</option>
                      <option value="rating">Avg. Rating</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="vp-divider" />

            {/* Skeletons */}
            {loading && (
              <div className="shop-pl-grid">
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && products.length === 0 && (
              <div className="vp-empty">
                <i
                  className="ph-bold ph-storefront"
                  style={{ fontSize: 48, color: "#ccc" }}
                />
                <p>No products found for this seller.</p>
                <button className="btn btn-main" onClick={() => navigate(-1)}>
                  Go Back
                </button>
              </div>
            )}

            {/* Products — same grid & card as ShopSectionOne */}
            {!loading && sorted.length > 0 && (
              <div className="shop-pl-grid">
                {sorted.map((item) => (
                  <ProductCard
                    key={item._id}
                    item={item}
                    selectedCurrency={selectedCurrency}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <FooterOne />

      <style>{`
        /* ══════════════════════════════════════
           Variables
           ══════════════════════════════════════ */
        .shop, .vp-root {
          --pl-primary: var(--main-600, #1a8a3c);
          --pl-border:  #e3e6e8;
          --pl-radius:  12px;
          --pl-text:    #111;
          --pl-sub:     #565959;
          --pl-price:   #b12704;
        }
        .vp-root {
          --vp-primary:    var(--main-600, #1a8a3c);
          --vp-primary-lt: var(--main-100, #e8f5ec);
          --vp-border:     #e3e6e8;
          background: #f7f8f8;
          min-height: 80vh;
        }

        /* ══════════════════════════════════════
           BANNER (unchanged from original)
           ══════════════════════════════════════ */
        .vp-banner { position: relative; }
        .vp-banner__cover {
          position: absolute; inset: 0;
          background-size: cover; background-position: center top;
          background-color: var(--vp-primary);
          background-image: linear-gradient(135deg, var(--main-800,#12602a) 0%, var(--vp-primary) 45%, var(--main-400,#3ab85e) 100%);
          overflow: hidden;
        }
        .vp-banner__blob { position: absolute; border-radius: 50%; opacity: 0.15; background: #fff; }
        .vp-banner__blob--1 { width: 450px; height: 450px; top: -180px; right: 4%; }
        .vp-banner__blob--2 { width: 280px; height: 280px; bottom: -120px; left: 8%; }
        .vp-banner__blob--3 { width: 150px; height: 150px; top: 30px; left: 40%; }
        .vp-banner__scrim {
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.58) 100%);
        }
        .vp-banner__body {
          position: relative; z-index: 2;
          display: flex; align-items: center; gap: 24px;
          padding-top: 40px; padding-bottom: 40px; flex-wrap: wrap;
        }
        .vp-banner__logo-wrap { flex-shrink: 0; }
        .vp-banner__logo-img {
          width: 90px; height: 90px; border-radius: 12px;
          border: 3px solid rgba(255,255,255,0.85);
          object-fit: cover; background: #fff;
          box-shadow: 0 4px 20px rgba(0,0,0,0.32);
        }
        .vp-banner__logo-placeholder {
          width: 90px; height: 90px; border-radius: 12px;
          border: 3px solid rgba(255,255,255,0.85);
          background: rgba(255,255,255,0.18);
          display: flex; align-items: center; justify-content: center;
          font-size: 42px; color: #fff;
          box-shadow: 0 4px 20px rgba(0,0,0,0.32);
        }
        .vp-banner__meta { flex: 1; min-width: 200px; }
        .vp-banner__name-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
        .vp-banner__store-name { font-size: 26px; font-weight: 800; color: #fff; margin: 0; letter-spacing: -0.3px; text-shadow: 0 2px 10px rgba(0,0,0,0.35); }
        .vp-banner__verified {
          display: inline-flex; align-items: center; gap: 4px;
          background: rgba(255,255,255,0.14); color: #a8f0c0;
          border: 1px solid rgba(168,240,192,0.4); border-radius: 20px;
          font-size: 11.5px; font-weight: 600; padding: 3px 11px; backdrop-filter: blur(4px);
        }
        .vp-banner__stats { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 18px; margin-bottom: 8px; }
        .vp-banner__stat { display: flex; align-items: center; gap: 5px; font-size: 13px; color: rgba(255,255,255,0.88); font-weight: 500; }
        .vp-banner__stat i { font-size: 14px; }

        /* ── Tabs ── */
        .vp-tabs-bar { background: #fff; border-bottom: 1px solid var(--vp-border); position: sticky; top: 0; z-index: 10; }
        .vp-tabs { display: flex; }
        .vp-tab { padding: 14px 22px; font-size: 14px; font-weight: 500; color: var(--pl-sub); background: none; border: none; border-bottom: 3px solid transparent; cursor: pointer; transition: all .18s; }
        .vp-tab--active { color: var(--pl-primary); border-bottom-color: var(--pl-primary); font-weight: 700; }

        /* ── Toolbar ── */
        .vp-page-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
        .vp-page-sub { font-size: 13px; color: var(--pl-sub); margin: 0; }
        .vp-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .vp-label { font-size: 12px; color: var(--pl-sub); margin-right: 6px; }
        .vp-select-wrap { display: flex; align-items: center; }
        .vp-select { border: 1px solid var(--pl-border); border-radius: 6px; padding: 6px 10px; font-size: 13px; color: var(--pl-text); background: #fff; cursor: pointer; outline: none; }
        .vp-select:focus { border-color: var(--pl-primary); }
        .vp-divider { height: 1px; background: var(--pl-border); margin-bottom: 24px; }

        /* ══════════════════════════════════════
           GRID — 3 cols (same as ShopSectionOne)
           ══════════════════════════════════════ */
        .shop-pl-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        /* ── Card (identical to ShopSectionOne) ── */
        .pl-card {
          background: #fff;
          border: 1px solid var(--pl-border);
          border-radius: var(--pl-radius);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transition: box-shadow .2s, border-color .2s;
        }
        .pl-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.11);
          border-color: #adb1b8;
        }

        /* ── Thumb ── */
        .pl-card__thumb {
          display: block;
          width: 100%;
          height: 210px;
          overflow: hidden;
          background: #f9fafb;
        }
        .pl-card__img {
          width: 100%; height: 100%;
          object-fit: cover;
          transition: transform .28s;
        }
        .pl-card:hover .pl-card__img { transform: scale(1.05); }

        /* ── Swatches ── */
        .pl-swatches {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 10px 4px;
          flex-wrap: wrap;
        }
        .pl-swatch {
          width: 20px; height: 20px;
          border-radius: 50%;
          border: 2px solid transparent;
          outline: 2px solid transparent;
          cursor: pointer; padding: 0;
          transition: outline .15s, border .15s, transform .15s;
          flex-shrink: 0;
        }
        .pl-swatch:hover {
          transform: scale(1.18);
          outline: 2px solid #aaa;
          outline-offset: 1px;
        }
        .pl-swatch--active {
          border: 2px solid #fff;
          outline: 2px solid var(--pl-primary);
          outline-offset: 1px;
          transform: scale(1.12);
        }
        .pl-swatch-more {
          font-size: 11.5px;
          color: var(--pl-primary);
          font-weight: 600;
          text-decoration: none;
          white-space: nowrap;
          margin-left: 2px;
        }
        .pl-swatch-more:hover { text-decoration: underline; }

        /* ── Content ── */
        .pl-card__content {
          padding: 4px 10px 12px;
          display: flex; flex-direction: column; gap: 4px; flex: 1;
        }
        .pl-card__name {
          margin: 0;
          font-size: 13.5px; font-weight: 600; line-height: 1.35;
        }
        .pl-card__name a {
          color: var(--pl-text); text-decoration: none;
          display: -webkit-box;
          -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
        }
        .pl-card__name a:hover { color: var(--pl-primary); text-decoration: underline; }

        .pl-card__rating { display: flex; align-items: center; gap: 3px; }
        .pl-rating__avg  { font-size: 12px; font-weight: 700; color: #555; }
        .pl-rating__star { font-size: 13px; color: #f0a500; display: flex; }
        .pl-rating__count{ font-size: 11.5px; color: var(--pl-sub); }

        .pl-card__price {
          display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px; margin-top: 2px;
        }
        .pl-price--now { font-size: 15px; font-weight: 700; color: var(--pl-price); }
        .pl-price--old { font-size: 12px; color: #888; text-decoration: line-through; }
        .pl-qty        { font-size: 11px; color: var(--pl-sub); font-weight: 400; }

        /* ── Empty ── */
        .vp-empty { text-align: center; padding: 60px 20px; color: #999; display: flex; flex-direction: column; align-items: center; gap: 14px; }

        /* ── Skeletons ── */
        @keyframes vp-shimmer { 0% { background-position: -600px 0; } 100% { background-position: 600px 0; } }
        .vp-skel { border-radius: 6px; background: linear-gradient(90deg,#ebebeb 25%,#f5f5f5 50%,#ebebeb 75%); background-size: 600px 100%; animation: vp-shimmer 1.4s infinite; }
        .vp-skeleton-card .pl-card__thumb { background: linear-gradient(90deg,#ebebeb 25%,#f5f5f5 50%,#ebebeb 75%); background-size: 600px 100%; animation: vp-shimmer 1.4s infinite; }
        .vp-skel--title  { height: 14px; width: 85%; margin-top: 4px; }
        .vp-skel--sub    { height: 12px; width: 55%; }
        .vp-skel--price  { height: 18px; width: 45%; }

        /* ══════════════════════════════════════
           RESPONSIVE
           ══════════════════════════════════════ */
        @media (max-width: 1199px) {
          .shop-pl-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 991px) {
          .shop-pl-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
          }
          .pl-card__thumb  { height: 160px; }
          .pl-card__name   { font-size: 12.5px; }
          .pl-price--now   { font-size: 13px; }
          .pl-card__rating { display: none; }
          .pl-price--old   { display: none; }
          .vp-banner__store-name { font-size: 20px; }
          .vp-banner__logo-img, .vp-banner__logo-placeholder { width: 72px; height: 72px; }
        }
        @media (max-width: 575px) {
          .shop-pl-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
          }
          .pl-card__thumb  { height: 130px; }
          .pl-card__content{ padding: 4px 7px 10px; }
          .pl-card__name   { font-size: 11.8px; }
          .pl-price--now   { font-size: 12.5px; }
          .pl-swatch       { width: 16px; height: 16px; }
          .pl-swatches     { gap: 4px; padding: 6px 7px 2px; }
          .pl-swatch-more  { font-size: 10.5px; }
          .vp-banner__body { padding-top: 22px; padding-bottom: 22px; gap: 14px; }
          .vp-banner__store-name { font-size: 18px; }
          .vp-banner__stats { gap: 4px 12px; }
        }
      `}</style>
    </>
  );
};

export default VendorProducts;
